import { useDemoMode } from '../composables/useDemoMode'
import { scrambleJson } from './nameScrambler'

const { active, viewAsTeacher, scrambleNames, nameMap } = useDemoMode()

const isApiPath = path => path.includes('/api/')
// Endpoints that resolve identity via the shared get_current_teacher
// dependency (backend/app/api/endpoints/auth.py), which already honors an
// admin-only `as_teacher` override — every GET here should preview the
// selected teacher's real data instead of the logged-in admin's own.
const AS_TEACHER_PATH_PREFIXES = ['/api/lessons/', '/api/teacher-availability/', '/api/admin/notification-prefs/', '/api/teacher-signups', '/api/contacts/roster', '/api/series-stops/mine']
const needsAsTeacher = path => AS_TEACHER_PATH_PREFIXES.some(prefix => path.includes(prefix))
// Demo Mode's own control-surface calls: must always see real, unscrambled
// data (auth/session, and the "preview as" teacher picker itself), otherwise
// re-opening the picker while already active would feed a fake name back in
// as the next `as_teacher` value.
const isExcludedPath = path => path.includes('/api/auth/') || path.includes('/api/admin/teachers')

function withAsTeacher(urlStr, teacherName) {
  const url = new URL(urlStr, window.location.origin)
  url.searchParams.set('as_teacher', teacherName)
  return urlStr.startsWith('http') ? url.toString() : url.pathname + url.search + url.hash
}

// Patches window.fetch so Demo Mode can, without touching any component:
//  - redirect GETs to AS_TEACHER_PATH_PREFIXES to a chosen teacher's real data
//  - scramble names/emails/phones in JSON responses
//  - short-circuit all non-GET /api/* calls so nothing actually persists
export function installDemoModeFetch() {
  const originalFetch = window.fetch.bind(window)

  window.fetch = async (input, init = {}) => {
    if (!active.value) return originalFetch(input, init)

    const isRequestObject = typeof Request !== 'undefined' && input instanceof Request
    const urlStr = isRequestObject ? input.url : String(input)
    const method = (init.method || (isRequestObject ? input.method : 'GET') || 'GET').toUpperCase()

    if (!isApiPath(urlStr) || isExcludedPath(urlStr)) {
      return originalFetch(input, init)
    }

    if (method !== 'GET') {
      // Feels like a real round-trip, but never reaches the backend — Demo
      // Mode's "clicks look like they work but don't persist" behavior.
      await new Promise(resolve => setTimeout(resolve, 200 + Math.random() * 100))
      return new Response(JSON.stringify({}), {
        status: 200,
        headers: { 'content-type': 'application/json' },
      })
    }

    let requestInput = input
    if (viewAsTeacher.value && needsAsTeacher(urlStr) && !isRequestObject) {
      requestInput = withAsTeacher(urlStr, viewAsTeacher.value.name)
    }

    const response = await originalFetch(requestInput, init)
    if (!scrambleNames.value) return response

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) return response

    const data = await response.clone().json().catch(() => null)
    if (data === null) return response

    return new Response(JSON.stringify(scrambleJson(data, nameMap)), {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    })
  }
}
